import { Entry, NavigationLink, NavigationSection } from '@oresundsbron/api';
import {
  Root,
  Item,
  List,
  Sub,
  Content,
} from '@radix-ui/react-navigation-menu';
import { Box } from '@oresundsbron/bridge-ui';
import * as Popover from '@radix-ui/react-popover';
import { pipe } from 'fp-ts/lib/function';
import {
  chain,
  fromNullable,
  toUndefined,
  map,
  alt,
  Option,
} from 'fp-ts/lib/Option';
import { findFirst, map as mapA } from 'fp-ts/lib/Array';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigation } from '../../hooks/useNavigation';
import { match } from 'fp-ts/lib/boolean';
import { useLinks } from '../../hooks/useLinks';
import { cx } from 'class-variance-authority';
import { useRouter } from 'next/router';
import { ChevronDown, ChevronRight } from '@oresundsbron/icons';
import Link from 'next/link';

const getTextColorClass = (level: number, isSelected: boolean) => {
  switch (level) {
    case 1:
      return `inline-block hover:no-underline hover:text-blue-800 py-3 ${
        isSelected ? 'text-blue-800' : 'text-neutral-600'
      }`;
    case 2:
      return `hover:border-l-2 pl-4 hover:border-l-blue-600 hover:-ml-0.5 inline-block py-2 hover:no-underline hover:text-blue-800 pl-4${
        isSelected
          ? 'text-blue-800 -ml-0.5 border-l-blue-600 border-l-2'
          : 'text-neutral-600'
      }`;
    default:
      return 'inline-block py-2 hover:no-underline hover:text-blue-800 text-neutral-600 pl-2 ';
  }
};

const isAnyChildActive =
  (predicate: (x: NavigationLink | NavigationSection) => boolean) =>
  (ll: (NavigationSection | NavigationLink)[]): Option<Entry> =>
    pipe(
      ll,
      findFirst(predicate),
      alt(() =>
        pipe(
          ll,
          findFirst((x) =>
            pipe(
              x.__typename === 'NavigationSection',
              match(
                () => false,
                () =>
                  pipe(
                    (x as NavigationSection).links,
                    isAnyChildActive(predicate),
                    toUndefined,
                    (res) => !!res
                  )
              )
            )
          )
        )
      )
    );

const LinkItem: FC<NavigationLink & { level: number }> = ({
  sys,
  title,
  pageLink,
  level,
}) => {
  const { getLink } = useLinks();
  const { asPath, locale } = useRouter();

  const href = useMemo(
    () =>
      pipe(
        pageLink,
        fromNullable,
        chain((x) => pipe(x.sys.id, getLink, fromNullable)),
        map((l) => `/${l.locale}${l.path}`),
        toUndefined
      ),
    [pageLink, getLink]
  );

  return (
    <Item value={sys.id}>
      {href && (
        <Link
          className={cx(
            getTextColorClass(level, `/${locale}${asPath}` === href),
            level === 1 ? 'ml-6' : undefined,
            `/${locale}${asPath}` === href
              ? 'text-blue-800'
              : 'text-neutral-600'
          )}
          href={href}
        >
          {title}
        </Link>
      )}
    </Item>
  );
};

const Section: FC<NavigationSection & { level: number }> = ({
  sys,
  title,
  links,
  pageLink,
  level,
}) => {
  const { getLink } = useLinks();
  const { asPath, locale } = useRouter();

  const items = useMemo(
    () =>
      pipe(
        links,
        fromNullable,
        map(mapA((link) => renderSectionOrLink(link, level + 1))),
        toUndefined
      ),
    [links, level]
  );

  const href = useMemo(
    () =>
      pipe(
        pageLink,
        fromNullable,
        chain((x) => pipe(x.sys.id, getLink, fromNullable)),
        map((l) => `/${l.locale}${l.path}`),
        toUndefined
      ),
    [pageLink, getLink]
  );

  const activeId = useMemo(
    () =>
      pipe(
        links,
        fromNullable,
        chain(
          isAnyChildActive(
            (x) => getLink(x.pageLink?.sys.id || '')?.path === asPath
          )
        ),
        map((l) => l.sys.id),
        toUndefined
      ),
    [links, getLink, asPath]
  );

  return (
    <Item value={sys.id}>
      <Box className="flex justify-between">
        {href && (
          <Link
            href={href as string}
            className={`group flex items-center ${getTextColorClass(
              level,
              `/${locale}${asPath}` === href
            )}`}
          >
            {level === 1 && <ChevronRight className="mr-2" />}
            {title}
          </Link>
        )}
      </Box>
      <Content
        className={`ml-6 ${level === 1 && 'border-l border-l-neutral-200'}`}
      >
        <Sub orientation="vertical" defaultValue={activeId} value={activeId}>
          <List>{items}</List>
        </Sub>
      </Content>
    </Item>
  );
};

const renderSectionOrLink = (x: Entry, level: number = 1) =>
  pipe(
    x.__typename === 'NavigationSection',
    match(
      () => (
        <LinkItem {...(x as NavigationLink)} level={level} key={x.sys.id} />
      ),
      () => (
        <Section {...(x as NavigationSection)} level={level} key={x.sys.id} />
      )
    )
  );

export const CorporateMeny: FC<{ className?: string }> = ({ className }) => {
  const { getLink } = useLinks();
  const { asPath, events } = useRouter();
  const [open, setOpen] = useState(false);
  const data = useNavigation('Corporate');

  const items = useMemo(
    () =>
      pipe(
        data,
        fromNullable,
        chain((d) => pipe(d.sections, fromNullable)),
        map(mapA(renderSectionOrLink)),
        toUndefined
      ),
    [data]
  );

  const activeId = useMemo(
    () =>
      pipe(
        data,
        fromNullable,
        map((d) => d.sections),
        chain(
          isAnyChildActive(
            (x) => getLink(x.pageLink?.sys.id || '')?.path === asPath
          )
        ),
        map((l) => l.sys.id),
        toUndefined
      ),
    [data, getLink, asPath]
  );

  useEffect(() => {
    const handleRouteChange = () => {
      setOpen(false);
    };

    events.on('routeChangeStart', handleRouteChange);
    return () => {
      events.off('routeChangeStart', handleRouteChange);
    };
  }, [events]);
  return (
    <div className={className && className}>
      <Root
        className={cx('hidden md:block')}
        orientation="vertical"
        value={activeId}
        defaultValue={activeId}
        aria-label={data?.title || ''}
      >
        <List>{items}</List>
      </Root>

      <Popover.Root open={open} onOpenChange={setOpen}>
        <div className="relative z-20 col-span-full wrapper-fix:!absolute wrapper-fix:!right-0 wrapper-fix:!top-0 wrapper-fix:mt-14 wrapper-fix:!transform-none">
          <Popover.Trigger asChild>
            <button className="mt-4 flex w-full items-center justify-center rounded-full border-2 border-neutral-200 bg-transparent p-2.5 md:mt-0 md:hidden">
              {data?.title}

              <ChevronDown className="ml-4 size-6" />
            </button>
          </Popover.Trigger>
          <Popover.Content
            className="left-0 right-0 top-full w-full rounded-xl border-2 border-neutral-200 bg-white px-5 py-3 shadow-lg"
            sideOffset={5}
          >
            <Root
              orientation="vertical"
              value={activeId}
              defaultValue={activeId}
              aria-label={data?.title || ''}
            >
              <List>{items}</List>
            </Root>
          </Popover.Content>
        </div>
      </Popover.Root>
    </div>
  );
};
